import React from 'react';

import { useDictionary } from '@/src/app/hooks';
import {
    MyLessons_SelectedIcon,
    MyLessons_UnselectedIcon,
    type UnitDTO,
    type ResourceItemDTO,
} from '@/src/ui';

interface LessonListProps {
    unit: UnitDTO;
    grade: string;
    currentUnit: number;
    extraItems: ResourceItemDTO[];
    myLessons: string[];
    setSelectedLessonIndex: (lesson: number | null) => void;
    addMyLesson: (lessonId: string) => void;
    removeMyLesson: (lessonId: string) => void;
}

const LessonList: React.FC<LessonListProps> = ({
    unit,
    grade,
    currentUnit,
    extraItems,
    myLessons,
    setSelectedLessonIndex,
    addMyLesson,
    removeMyLesson,
}) => {
    const { translate } = useDictionary();
    if (!unit.lessons || unit.lessons.length === 0) {
        return null;
    }
    const splitIndex = Math.ceil(unit.lessons.length / 2);

    const column1Children: React.JSX.Element[] = [];
    const column2Children: React.JSX.Element[] = [];

    unit.lessons.forEach((lesson, lessonIndex) => {
        const unitIndex = currentUnit;

        const lessonId = `G${grade}_U${unitIndex}_L${lessonIndex}`;
        const isSaved = myLessons && myLessons?.includes(lessonId);
        const SvgIcon = isSaved ? MyLessons_SelectedIcon : MyLessons_UnselectedIcon;
        const onClick = isSaved ? () => removeMyLesson(lessonId) : () => addMyLesson(lessonId);

        const lessonDiv = (
            <div className="lesson">
                <a
                    href="#"
                    className={`lesson-link pd-G${grade}-lesson-link`}
                    onClick={(e) => {
                        e.preventDefault();
                        setSelectedLessonIndex(lessonIndex);
                    }}
                >
                    <span className="lesson-number-line">
                        {translate('Lesson')} {lesson.lessonNumber}:
                        {lesson.lessonType && (
                            <span className={`lesson-type-label pd-G${grade}-primary background`}>
                                {translate(lesson.lessonType)}
                            </span>
                        )}
                    </span>
                    {lesson.title}
                </a>
                <a
                    id={`G${grade}lesson${lessonIndex}myLessonsButton`}
                    className="myLessonsButton"
                    data-lesson-id={`G${grade}_U${currentUnit}_L${lessonIndex}`}
                    onClick={onClick}
                >
                    <SvgIcon color={`g${grade}`} />
                </a>
            </div>
        );

        if (lessonIndex < splitIndex) {
            column1Children.push(lessonDiv);
        } else {
            column2Children.push(lessonDiv);
        }
    });

    let index = 0;
    return (
        <>
            {extraItems.length > 0 && (
                <>
                    <div className="slides-buttons">
                        {extraItems.map((extraItem) => (
                            <a
                                key={extraItem.key ?? extraItem.text}
                                href={extraItem.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`slides-button pd-G${grade}-primary background`}
                            >
                                {extraItem.text}
                            </a>
                        ))}
                    </div>
                    <hr className={`slides-divider pd-G${grade}-primary background`} />
                </>
            )}
            <div className="lesson-columns row">
                <div className="lesson-column col-12 col-md-6 lesson-col-1">
                    {column1Children.map((c) => (
                        <React.Fragment key={`lesson-col-1-${index++}`}>{c}</React.Fragment>
                    ))}
                </div>
                <div className="lesson-column col-12 col-md-6 lesson-col-2">
                    {column2Children.map((c) => (
                        <React.Fragment key={`lesson-col-2-${index++}`}>{c}</React.Fragment>
                    ))}
                </div>
            </div>
        </>
    );
};

export default LessonList;
